/**
 * Moderación (1.0 Moderation + AdultBackfillPanel): cola `pending_review` (más antiguos primero),
 * rechazados y +18 (`is_adult`). Aprobar / rechazar con motivo (declaración DSA al vendedor) /
 * forzar +18 o seguro / quitar la anulación. Todo pasa por `admin-moderateListing`.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { env } from '../env.js';
import { db } from '../firebase.js';
import { btn, muted, Notice, str, Table, td } from '../lib/ui.js';

type Doc = Record<string, unknown>;
type Queue = 'pending_review' | 'rejected' | 'adult';

function photo(p: Doc): string | null {
  const ph = (p.photos as unknown[] | undefined)?.[0];
  if (typeof ph === 'string') return ph;
  if (ph && typeof ph === 'object' && typeof (ph as Doc).url === 'string')
    return (ph as Doc).url as string;
  return null;
}

export function ModerationPage(): ReactElement {
  const [queue, setQueue] = useState<Queue>('pending_review');
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    setRows(null);
    const q =
      queue === 'adult'
        ? query(
            collection(db, 'products'),
            where('is_adult', '==', true),
            orderBy('updated_date', 'asc'),
            limit(100),
          )
        : query(
            collection(db, 'products'),
            where('status', '==', queue),
            orderBy('updated_date', 'asc'),
            limit(100),
          );
    getDocs(q)
      .then((s) => {
        setRows(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch((e: unknown) => {
        setError(errorText(e));
        setRows([]);
      });
  }, [queue, version]);

  const act = (
    id: string,
    action: 'approve' | 'reject' | 'adult' | 'safe' | 'clear_override',
  ): void => {
    let reason = '';
    if (action === 'reject') {
      reason = window.prompt('Motivo del rechazo (lo ve el vendedor, declaración DSA):') ?? '';
      if (!reason) return;
    }
    setError(null);
    call(api.admin.moderateListing, { product_id: id, action, ...(reason ? { reason } : {}) })
      .then(() => {
        setNotice('Hecho.');
        setVersion((v) => v + 1);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };

  return (
    <>
      <h1 style={{ margin: 0 }}>Moderación</h1>
      <p style={muted}>
        Pendientes = `pending_review` (revisión humana activada en Ajustes → featureFlags). Rechazar
        envía al vendedor la declaración de motivos (DSA) y queda en `moderationDecisions`.
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        {(['pending_review', 'rejected', 'adult'] as const).map((q) => (
          <button
            key={q}
            type="button"
            style={btn(queue === q)}
            onClick={() => {
              setQueue(q);
            }}
          >
            {q === 'pending_review' ? 'Pendientes' : q === 'rejected' ? 'Rechazados' : '+18'}
          </button>
        ))}
      </div>
      <Notice text={notice} />
      <Notice text={error} error />
      {!rows && <p style={muted}>Cargando…</p>}
      {rows && (
        <Table
          head={['Foto', 'Anuncio', 'Vendedor', 'Precio', 'Estado', 'Motivo', '+18', 'Acciones']}
        >
          {rows.map((p) => (
            <tr key={str(p.id)}>
              <td style={td}>
                {photo(p) && (
                  <img
                    src={photo(p) ?? ''}
                    alt=""
                    style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8 }}
                  />
                )}
              </td>
              <td style={td}>
                <a
                  href={`${env.VITE_WEB_ORIGIN}/es/p/${str(p.id)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {str(p.title)}
                </a>
                <div style={{ ...muted, fontSize: 12 }}>
                  {str((p.category as Doc | undefined)?.category_id)} ·{' '}
                  {((p.description as string | undefined) ?? '').slice(0, 120)}
                </div>
              </td>
              <td style={td}>
                <Link to={`/usuarios/${str(p.seller_id)}`}>
                  @{str(p.seller_username) || str(p.seller_id).slice(-6)}
                </Link>
              </td>
              <td style={td}>
                {str(p.price)} {str(p.currency)}
              </td>
              <td style={td}>{str(p.status)}</td>
              <td style={td}>{str(p.moderation_reason)}</td>
              <td style={td}>
                {p.is_adult === true ? 'sí' : 'no'}
                {typeof p.adult_override === 'string' ? ` (forzado: ${p.adult_override})` : ''}
              </td>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>
                {p.status !== 'active' && (
                  <button
                    type="button"
                    style={btn(true)}
                    onClick={() => {
                      act(str(p.id), 'approve');
                    }}
                  >
                    Aprobar
                  </button>
                )}{' '}
                {p.status !== 'rejected' && (
                  <button
                    type="button"
                    style={btn(false, true)}
                    onClick={() => {
                      act(str(p.id), 'reject');
                    }}
                  >
                    Rechazar
                  </button>
                )}{' '}
                {p.is_adult !== true && (
                  <button
                    type="button"
                    style={btn(false)}
                    onClick={() => {
                      act(str(p.id), 'adult');
                    }}
                  >
                    +18
                  </button>
                )}{' '}
                {p.is_adult === true && (
                  <button
                    type="button"
                    style={btn(false)}
                    onClick={() => {
                      act(str(p.id), 'safe');
                    }}
                  >
                    Seguro
                  </button>
                )}{' '}
                {typeof p.adult_override === 'string' && (
                  <button
                    type="button"
                    style={btn(false)}
                    onClick={() => {
                      act(str(p.id), 'clear_override');
                    }}
                  >
                    Quitar forzado
                  </button>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td style={td} colSpan={8}>
                Nada en esta cola.
              </td>
            </tr>
          )}
        </Table>
      )}
    </>
  );
}
